import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGetSearchProductListQuery } from "../../utils/api/product/product.api";
import type { Product } from "../../utils/api/product/product.type";
import { usePagination } from "../../utils/hooks";
import { QueryUrl } from "../../utils/Utils";
import { BestSellerCard } from "../HomePage/BestSellerCard";
import FrontFooter from "../shared/FrontFooter/FrontFooter";
import FrontHeader from "../shared/FrontHeader/FrontHeader";
import FrontMainNav from "../shared/FrontMainNav/FrontMainNav";
import FilterItems from "../user/ParcoursAchat/FilterItems";
import FilterMobileItem from "../user/ParcoursAchat/modal/FilterMobileItem";
import { ProductItemsSkeleton } from "../user/ParcoursAchat/ParcoursAchat";

const LIMIT = 9;

function SearchResultsPage() {
  const { page, setPage } = usePagination(LIMIT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(true);

  const q = searchParams.get("q") || "";

  const [query, setQuery] = useState({
    categorie: searchParams?.get("categorie") || "",
    sous_categorie: searchParams?.get("sous_categorie") || "",
    prix_min: searchParams?.get("prix_min") || "",
    prix_max: searchParams?.get("prix_max") || "",
    orderBy: searchParams?.get("orderBy") || "",
    q,
  });

  useEffect(() => {
    setQuery((prev) => ({ ...prev, q }));
  }, [q]);

  const queryArgs = useMemo(
    () => ({
      page,
      limit: LIMIT,
      q,
      prix_min: searchParams?.get("prix_min") || "",
      prix_max: searchParams?.get("prix_max") || "",
      categorie: searchParams?.get("categorie") || "",
      sous_categorie: (() => {
        const raw = searchParams?.get("sous_categorie");
        if (!raw) return "";
        try {
          return JSON.parse(raw);
        } catch {
          return "";
        }
      })(),
    }),
    [
      page,
      q,
      searchParams?.get("prix_min"),
      searchParams?.get("prix_max"),
      searchParams?.get("categorie"),
      searchParams?.get("sous_categorie"),
    ]
  );

  const { data = { results: [], count: 0 }, isFetching } =
    useGetSearchProductListQuery(queryArgs);

  const lastSyncedRef = useRef<{ page: number; count: number } | null>(null);

  useEffect(() => {
    if (!data?.results) return;

    const results = data.results;
    const count = data?.count ?? 0;

    if (
      lastSyncedRef.current?.page === page &&
      lastSyncedRef.current?.count === count
    ) {
      return;
    }
    lastSyncedRef.current = { page, count };

    if (page === 1) {
      setAllProducts(results);
      setHasMore(results.length > 0 && results.length < count);
    } else {
      setAllProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p?.id));
        const newItems = results.filter(
          (p) => p?.id != null && !existingIds.has(p.id)
        );
        if (!newItems.length) {
          setHasMore(false);
          return prev;
        }
        return [...prev, ...newItems];
      });
    }
  }, [page, data]);

  const filtersKey =
    (searchParams?.get("categorie") ?? "") +
    (searchParams?.get("sous_categorie") ?? "") +
    (searchParams?.get("prix_min") ?? "") +
    (searchParams?.get("prix_max") ?? "") +
    q;

  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    lastSyncedRef.current = null;
    setHasMore(true);
  }, [filtersKey]);

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    setPage((p) => p + 1);
  }, [isFetching, hasMore, setPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const priceExtremes = useMemo(() => {
    if (!allProducts.length) return null;
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const p of allProducts) {
      const raw = (p as any)?.prix ?? (p as any)?.price ?? null;
      const price = raw != null ? Number(raw) : NaN;
      if (!Number.isFinite(price)) continue;
      if (price < min) min = price;
      if (price > max) max = price;
    }
    if (!Number.isFinite(min)) return null;
    return { min, max };
  }, [allProducts]);

  return (
    <div className="landing-page-component">
      <FrontHeader />
      <div className="page-content">
        <FrontMainNav />
        <div className="container-easy-market">
          <div className="row row-achat-page mb-5">
            <FilterItems
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              query={query}
              setQuery={setQuery}
              setPage={setPage}
              minAvailablePrice={priceExtremes?.min}
              maxAvailablePrice={priceExtremes?.max}
            />
            <div className="container-filter-parcours-achat-mobile no-view-desktop">
              <button
                className="btn btn-filter-mobile"
                type="button"
                onClick={() => setShow(true)}
              >
                Filtrer
              </button>
            </div>
            <div className="col-lg-9 col-md-7 col-right-achat-page">
              <div className="content-col-right-achat-page">
                <div className="container-home-page mb-3 our-product-container-homepage our-product-grid-3 best-sellers-section">
                  <div className="best-sellers-section-container mt-3">
                    <div className="best-sellers-section-header">
                      <h3 className="title-home-page-theme text-center">
                        Résultats de recherche
                      </h3>

                      

                      {!!q && (
                        <p className="search-results-query text-center">
                          pour « {q} »
                        </p>
                      )}
                    </div>

                    <div className="best-sellers-section-grid our-product-grid">
                      {page === 1 && isFetching && <ProductItemsSkeleton />}
                      {allProducts.length > 0 &&
                        allProducts.map((product, index) => (
                          <div
                            key={
                              product?.id != null
                                ? `search-product-${product.id}-${index}`
                                : `search-product-fallback-${index}`
                            }
                            className="best-sellers-section-grid-item"
                          >
                            <BestSellerCard item={product} />
                          </div>
                        ))}
                    </div>

                    {!isFetching && allProducts.length === 0 && (
                      <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                        <p>Aucun produit trouvé pour cette recherche.</p>
                      </div>

                      
                    )}
                    {(searchParams.get("categorie") || q) && (
                        <div className="d-flex justify-content-center mt-2 mb-3">
                           <Link
  to="/produits"
  className="btn btn-purple btn-sm d-flex align-items-center gap-2 px-3 py-2 mt-3"
  onClick={() => {
    setSearchParams(
      QueryUrl("", {
        ...query,
        categorie: "",
        sous_categorie: [],
      })
    );
    setPage(1);
  }}
>
  <i className="bi bi-arrow-left"></i>
  Tous les produits
                          </Link>
                        </div>
                      )}

                    <div
                      ref={sentinelRef}
                      className="infinite-scroll-sentinel"
                      aria-hidden="true"
                    />

                    {isFetching && page > 1 && hasMore && (
                      <div className="d-flex justify-content-center py-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Chargement...</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FrontFooter />
      <FilterMobileItem
        show={show}
        setShow={setShow}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        query={query}
        setQuery={setQuery}
        setPage={setPage}
      />
    </div>
  );
}

export default SearchResultsPage;